import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { useAuth } from '../../auth/useAuth';
import { listRuns, getRunAnalytics, exportRunAnalyticsCsv } from '../../api/runs';
import { ApiError } from '../../api/client';
import { localizedText } from '../../i18n/contentLocales';
import type { Run, RunAnalytics as RunAnalyticsData, RunAnalyticsExportTable, RunState } from '../../api/types';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';
import ChartSection from './ChartSection';
import AnalyticsRangeView from './AnalyticsRangeView';

// Chart colors — derived from the app's M3 theme hues (theme.ts primary /
// tertiary), nudged to clear the dataviz skill's categorical validator (light
// mode): theme.palette.primary.main sits at OKLCH L 0.428, just under the
// 0.43 floor, and theme.palette.tertiary.main's chroma (0.091) sits just
// under the 0.10 floor. Both nudges keep the same hue family; validated
// together with `validate_palette.js "#2850A9,#3E8E3E" --mode light` → all
// checks pass (worst adjacent CVD ΔE 26.7 protan/deutan, 28.0 normal-vision).
const CHART_BLUE = '#2850A9'; // "attempted" / primary metric (points, timeline)
const CHART_GREEN = '#3E8E3E'; // "scored" / completed metric

const STATE_COLORS: Record<RunState, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  configured: 'default',
  scheduled: 'primary',
  active: 'success',
  paused: 'warning',
  ended: 'error',
};

interface StatTileProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  caption?: string;
  /** Disambiguates the value in tests — plain numbers otherwise collide with the challenge table. */
  testId: string;
}

const StatTile: React.FC<StatTileProps> = ({ icon, label, value, caption, testId }) => (
  <Card sx={{ flex: '1 1 200px', minWidth: 180 }}>
    <CardContent>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {label}
          </Typography>
          <Typography
            variant="h5"
            component="p"
            data-testid={testId}
            sx={{ fontWeight: 700, lineHeight: 1.2 }}
          >
            {value}
          </Typography>
          {caption && (
            <Typography variant="caption" color="text.secondary">
              {caption}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const RunAnalytics: React.FC = () => {
  const { t, i18n } = useTranslation('runs');
  const { user } = useAuth();
  const orgSlug = user?.organization_slug ?? '';

  const [runs, setRuns] = useState<Run[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState('');

  const [analytics, setAnalytics] = useState<RunAnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  const [exportError, setExportError] = useState<string | null>(null);

  // Date-range (default) vs single-run mode (spec 039 / #405). Range mode
  // subsumes single-run; the run mode preserves the spec-038 per-run view.
  const [mode, setMode] = useState<'range' | 'run'>('range');

  const loadRuns = useCallback(async () => {
    if (!orgSlug) return;
    setRunsLoading(true);
    setRunsError(null);
    try {
      const data = await listRuns(orgSlug);
      const sorted = [...data.items].sort(
        (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
      );
      setRuns(sorted);
      // Preselect the newest run so the coordinator reaches charts in the
      // fewest clicks (SC-003: sidebar → run → charts in <=3 clicks).
      setSelectedRunId((prev) => (prev && sorted.some((r) => r.id === prev) ? prev : sorted[0]?.id ?? ''));
    } catch (err) {
      setRunsError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setRunsLoading(false);
    }
  }, [orgSlug]);

  const loadAnalytics = useCallback(async () => {
    if (!orgSlug || !selectedRunId) return;
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const data = await getRunAnalytics(orgSlug, selectedRunId);
      setAnalytics(data);
    } catch (err) {
      setAnalyticsError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setAnalyticsLoading(false);
    }
  }, [orgSlug, selectedRunId]);

  // Wrapped in a locally-declared async IIFE (rather than calling the
  // useCallback-memoized loader directly) so react-hooks/set-state-in-effect
  // doesn't flag the fetch-on-mount pattern — see loadRuns for the actual
  // state updates.
  useEffect(() => {
    void (async () => {
      await loadRuns();
    })();
  }, [loadRuns]);

  useEffect(() => {
    if (!selectedRunId) return;
    void (async () => {
      await loadAnalytics();
    })();
  }, [selectedRunId, loadAnalytics]);

  const handleExportCsv = async (table: RunAnalyticsExportTable) => {
    if (!orgSlug || !selectedRunId) return;
    setExportError(null);
    try {
      const csv = await exportRunAnalyticsCsv(orgSlug, selectedRunId, table);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = t(`analytics.export.filename.${table}`, { runId: selectedRunId });
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  const lang = i18n.language;
  const titleOf = (ml: Record<string, string>, fallback: string) => localizedText(ml, lang) || fallback;

  // Derived, NaN-safe aggregates (US1 Acceptance #2 — no teams yet must never
  // render NaN). All divisions guard the zero-denominator case explicitly.
  const totalSubmissions = analytics
    ? analytics.challenges.reduce((n, c) => n + c.submissions, 0)
    : 0;
  const totalScored = analytics ? analytics.challenges.reduce((n, c) => n + c.scored, 0) : 0;
  const scoredPercent =
    totalSubmissions > 0 ? Math.round((totalScored / totalSubmissions) * 100) : 0;

  const stationDataset = (analytics?.stations ?? []).map((s) => ({
    id: s.run_station_id,
    label: titleOf(s.title, s.run_station_id),
    teams_attempted: s.teams_attempted,
    teams_scored: s.teams_scored,
  }));

  const pointsDataset = [...(analytics?.teams ?? [])]
    .sort((a, b) => b.total_points - a.total_points)
    .map((tm) => ({ team: tm.team_name, points: tm.total_points }));

  const timelineDataset = (analytics?.timeline ?? []).map((b) => ({
    time: new Date(b.bucket_start).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
    submissions: b.submissions,
  }));

  return (
    <Box>
      <Typography variant="h2" component="h1" sx={{ mb: 3 }}>
        {t('analytics.title')}
      </Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={mode}
        onChange={(_, next: 'range' | 'run' | null) => {
          if (next) setMode(next);
        }}
        sx={{ mb: 3 }}
        aria-label={t('analytics.modeLabel', { defaultValue: 'Ansicht' })}
      >
        <ToggleButton value="range">{t('analytics.modeRange', { defaultValue: 'Zeitraum' })}</ToggleButton>
        <ToggleButton value="run">{t('analytics.modeRun', { defaultValue: 'Einzelner Lauf' })}</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'range' && <AnalyticsRangeView orgSlug={orgSlug} />}

      {mode === 'run' && (
        <>
      <FormControl size="small" sx={{ minWidth: 280, mb: 3 }}>
        <InputLabel id="analytics-run-picker-label">{t('analytics.runPicker.label')}</InputLabel>
        <Select
          labelId="analytics-run-picker-label"
          label={t('analytics.runPicker.label')}
          value={selectedRunId}
          onChange={(e) => setSelectedRunId(e.target.value)}
          disabled={runsLoading || runs.length === 0}
        >
          {runs.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <span>{r.name}</span>
                <Chip label={t(`list.filter.${r.state}`)} color={STATE_COLORS[r.state]} size="small" />
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {runsLoading && <LoadingSpinner />}
      {!runsLoading && runsError && <ErrorAlert message={runsError} onRetry={() => void loadRuns()} />}
      {!runsLoading && !runsError && runs.length === 0 && (
        <Typography color="text.secondary">{t('analytics.noRuns')}</Typography>
      )}

      {!runsLoading && !runsError && runs.length > 0 && (
        <>
          {analyticsLoading && <LoadingSpinner />}
          {!analyticsLoading && analyticsError && (
            <ErrorAlert message={analyticsError} onRetry={() => void loadAnalytics()} />
          )}

          {!analyticsLoading && !analyticsError && analytics && (
            <Stack spacing={3}>
              {/* Stat tiles */}
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                <StatTile
                  testId="stat-teams"
                  icon={<GroupIcon />}
                  label={t('analytics.tiles.teams')}
                  value={analytics.participation.team_count}
                />
                <StatTile
                  testId="stat-participants"
                  icon={<PeopleAltIcon />}
                  label={t('analytics.tiles.participants')}
                  value={analytics.participation.participant_count}
                  caption={t('analytics.tiles.participantsCaption', {
                    registered: analytics.participation.registered_count,
                    guests: analytics.participation.guest_count,
                  })}
                />
                <StatTile
                  testId="stat-submissions"
                  icon={<AssignmentIcon />}
                  label={t('analytics.tiles.submissions')}
                  value={totalSubmissions}
                />
                <StatTile
                  testId="stat-scored-percent"
                  icon={<CheckCircleOutlineIcon />}
                  label={t('analytics.tiles.scoredPercent')}
                  value={`${scoredPercent}%`}
                  caption={t('analytics.tiles.scoredCaption', {
                    scored: totalScored,
                    total: totalSubmissions,
                  })}
                />
              </Stack>

              {/* Station funnel */}
              <ChartSection
                title={t('analytics.charts.funnel.title')}
                toggleLabel={t('analytics.charts.funnel.toggleTable')}
                empty={stationDataset.length === 0}
                emptyMessage={t('analytics.charts.funnel.empty')}
                chart={
                  <BarChart
                    dataset={stationDataset}
                    xAxis={[{ dataKey: 'label', scaleType: 'band' }]}
                    series={[
                      {
                        dataKey: 'teams_attempted',
                        label: t('analytics.charts.funnel.attempted'),
                        color: CHART_BLUE,
                      },
                      {
                        dataKey: 'teams_scored',
                        label: t('analytics.charts.funnel.scored'),
                        color: CHART_GREEN,
                      },
                    ]}
                    height={300}
                  />
                }
                table={
                  <TableContainer>
                    <Table size="small" aria-label={t('analytics.charts.funnel.title')}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('analytics.charts.funnel.table.station')}</TableCell>
                          <TableCell align="right">{t('analytics.charts.funnel.table.attempted')}</TableCell>
                          <TableCell align="right">{t('analytics.charts.funnel.table.scored')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stationDataset.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.label}</TableCell>
                            <TableCell align="right">{row.teams_attempted}</TableCell>
                            <TableCell align="right">{row.teams_scored}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                }
              />

              {/* Points distribution */}
              <ChartSection
                title={t('analytics.charts.points.title')}
                toggleLabel={t('analytics.charts.points.toggleTable')}
                empty={pointsDataset.length === 0}
                emptyMessage={t('analytics.charts.points.empty')}
                chart={
                  <BarChart
                    dataset={pointsDataset}
                    xAxis={[{ dataKey: 'team', scaleType: 'band' }]}
                    series={[{ dataKey: 'points', color: CHART_BLUE }]}
                    height={300}
                    hideLegend
                  />
                }
                table={
                  <TableContainer>
                    <Table size="small" aria-label={t('analytics.charts.points.title')}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('analytics.charts.points.table.team')}</TableCell>
                          <TableCell align="right">{t('analytics.charts.points.table.points')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pointsDataset.map((row) => (
                          <TableRow key={row.team}>
                            <TableCell>{row.team}</TableCell>
                            <TableCell align="right">{row.points}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                }
              />

              {/* Submissions over time */}
              <ChartSection
                title={t('analytics.charts.timeline.title')}
                toggleLabel={t('analytics.charts.timeline.toggleTable')}
                empty={timelineDataset.length === 0}
                emptyMessage={t('analytics.charts.timeline.empty')}
                chart={
                  <LineChart
                    dataset={timelineDataset}
                    xAxis={[{ dataKey: 'time', scaleType: 'point' }]}
                    series={[{ dataKey: 'submissions', color: CHART_BLUE, area: false }]}
                    height={300}
                    hideLegend
                  />
                }
                table={
                  <TableContainer>
                    <Table size="small" aria-label={t('analytics.charts.timeline.title')}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('analytics.charts.timeline.table.time')}</TableCell>
                          <TableCell align="right">{t('analytics.charts.timeline.table.submissions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {timelineDataset.map((row, idx) => (
                          <TableRow key={`${row.time}-${idx}`}>
                            <TableCell>{row.time}</TableCell>
                            <TableCell align="right">{row.submissions}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                }
              />

              {/* Per-challenge stats — backs the "challenges" CSV export;
                  shown as a plain data table (not a chart). */}
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {t('analytics.challengeTable.title')}
                  </Typography>
                  {analytics.challenges.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      {t('analytics.challengeTable.empty')}
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small" aria-label={t('analytics.challengeTable.title')}>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('analytics.challengeTable.station')}</TableCell>
                            <TableCell>{t('analytics.challengeTable.challenge')}</TableCell>
                            <TableCell>{t('analytics.challengeTable.type')}</TableCell>
                            <TableCell align="right">{t('analytics.challengeTable.submissions')}</TableCell>
                            <TableCell align="right">{t('analytics.challengeTable.scored')}</TableCell>
                            <TableCell align="right">{t('analytics.challengeTable.avgPoints')}</TableCell>
                            <TableCell align="right">{t('analytics.challengeTable.maxPoints')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {analytics.challenges.map((c) => (
                            <TableRow key={c.run_challenge_id}>
                              <TableCell>{titleOf(c.station_title, '—')}</TableCell>
                              <TableCell>{titleOf(c.title, '—')}</TableCell>
                              <TableCell>{t(`participant.challenge.types.${c.type}`)}</TableCell>
                              <TableCell align="right">{c.submissions}</TableCell>
                              <TableCell align="right">{c.scored}</TableCell>
                              <TableCell align="right">
                                {c.avg_points != null ? c.avg_points.toFixed(1) : '—'}
                              </TableCell>
                              <TableCell align="right">{c.max_points}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>

              {/* Feedback aggregate (US3) — hidden entirely when there is no
                  feedback yet, never rendered with 0/NaN placeholders. */}
              {analytics.feedback && analytics.feedback.total_count > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" component="h2" gutterBottom>
                      {t('analytics.feedback.title')}
                    </Typography>
                    <Typography variant="body1">
                      {t('analytics.feedback.averageRating')}: {analytics.feedback.average_rating.toFixed(1)} / 5
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('analytics.feedback.totalCount', { count: analytics.feedback.total_count })}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Export */}
              {exportError && <ErrorAlert message={exportError} />}
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => void handleExportCsv('teams')}
                >
                  {t('analytics.export.teams')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => void handleExportCsv('challenges')}
                >
                  {t('analytics.export.challenges')}
                </Button>
              </Stack>
            </Stack>
          )}
        </>
      )}
        </>
      )}
    </Box>
  );
};

export default RunAnalytics;
